import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { CreditCard, Eye, Pencil, Search, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import MobileCrudList from '../../component/common/MobileCrud';
import { protoIcon } from '../../component/common/ProtoIcon';
import { KitPill } from '../../component/common/MobileKit';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import usePermissions from '../../hooks/usePermissions';
import CardMachineViewDialog from './View';

// Ícone de maquininha (`.mach-ic` de `prototipo-lista-maquininhas.html`) —
// mesmo badge circular usado na coluna "Nome" e no header do dialog de
// visualização.
function MachineIcon({ size = 32 }) {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: '9px',
                bgcolor: 'info.bg',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <CreditCard size={16} strokeWidth={2} />
        </Box>
    );
}

const CardIcon = protoIcon('card');
const EyeIcon = protoIcon('eye');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');

// Taxa da API ("1,19" ou número) -> "1,19"
const taxText = (value) => String(value ?? '—');

export default function CardMachinesList() {
    const theme = useTheme();
    const isMobileShell = useIsMobileShell();

    const [loading, setLoading] = useState(true);
    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [cardMachineToDelete, setCardMachineToDelete] = useState(null);
    const [cardMachineNameToDelete, setCardMachineNameToDelete] = useState('');
    const [search, setSearch] = useState('');
    const [data, setData] = useState([]);
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedCardMachineId, setSelectedCardMachineId] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();

    const fetchData = async () => {
        try {
            const response = await httpClient.get('/cardmachines');
            setData(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching card machines:', error);
            setData([]);
        }
    };

    useEffect(() => {
        fetchData().finally(() => {
            setLoading(false);
        });
    }, []);

    const handleChangeSearch = (value) => {
        setSearch(value);
    };

    const filteredData = useMemo(() => {
        if (!search) return data;
        const term = search.toLowerCase();
        return data.filter((cardMachine) =>
            (cardMachine.Name || '').toLowerCase().includes(term) ||
            String(cardMachine.MaxAmountInstallment ?? '').includes(term) ||
            String(cardMachine.TaxDebit ?? '').includes(term) ||
            String(cardMachine.TaxPixQrCode ?? '').includes(term)
        );
    }, [data, search]);

    const handleClearFilters = () => {
        setSearch('');
    };

    // "Nova maquininha"/"Editar" navegam para o formulário real de maquininha
    // (`/cardmachines/new`, `/cardmachines/edit/:id`), mesmo precedente
    // adotado em Serviços/Produtos — ver FRONTEND-TASKS.md "Formulário de
    // maquininha".
    const handleNewCardMachine = () => {
        navigate('/cardmachines/new');
    };

    const handleEditCardMachine = (row) => {
        navigate(`/cardmachines/edit/${row.ID}`);
    };

    const handleDeleteCardMachine = async () => {
        setLoading(true);
        try {
            await httpClient.delete(`/cardmachines/${cardMachineToDelete}`);
            setOpenDialogConfirmDelete(false);
            setData((prev) => prev.filter((item) => item.ID !== cardMachineToDelete));
            setSnackbarMessage('Maquininha excluída com sucesso!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Error deleting card machine:', error);
            setSnackbarMessage('Erro ao excluir maquininha');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setCardMachineToDelete(null);
            setCardMachineNameToDelete('');
            setLoading(false);
        }
    };

    const columns = [
        {
            name: 'Nome',
            path: 'Name',
            render: (row) => (
                <Box sx={{ alignItems: 'center', gap: 1.25, justifyContent: 'flex-start' }}>
                    
                    <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }} title={row.Name}>
                        {row.Name}
                    </Typography>
                </Box>
            )
        },
        {
            name: 'Máx. parcelas',
            path: 'MaxAmountInstallment',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.MaxAmountInstallment}x
                </Typography>
            )
        },
        {
            name: 'Taxa débito',
            path: 'TaxDebit',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.TaxDebit}%
                </Typography>
            )
        },
        {
            name: 'Taxa PIX',
            path: 'TaxPixQrCode',
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>
                    {row.TaxPixQrCode}%
                </Typography>
            )
        }
    ];

    const actions = [
        ...(hasPermission('cardmachine.view') ? [{
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    setSelectedCardMachineId(row.ID);
                    setOpenViewDialog(true);
                },
                tooltip: 'Visualizar',
            }
        }] : []),
        ...(hasPermission('cardmachine.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    handleEditCardMachine(row);
                },
                tooltip: 'Editar',
            }
        }] : []),
        ...(hasPermission('cardmachine.delete') ? [{
            icon: <Trash2 size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => {
                    setCardMachineToDelete(row.ID);
                    setCardMachineNameToDelete(row.Name);
                    setOpenDialogConfirmDelete(true);
                },
                tooltip: 'Excluir',
            }
        }] : []),
    ];

    const sharedDialogs = (
        <>
            <DialogForm
                iconType={'warning'}
                open={openDialogConfirmDelete}
                title={'Atenção'}
                confirmButtonText={'Sim, Excluir'}
                content={`Tem certeza que deseja excluir a maquininha "${cardMachineNameToDelete}"?`}
                secondaryContent={'Esta ação não pode ser desfeita.'}
                onSubmit={() => {
                    handleDeleteCardMachine();
                }}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setCardMachineToDelete(null);
                    setCardMachineNameToDelete('');
                }}
                loading={loading}
                loadingText="Removendo..."
            />

            <CardMachineViewDialog
                open={openViewDialog}
                onClose={() => {
                    setOpenViewDialog(false);
                    setSelectedCardMachineId(null);
                }}
                cardMachineId={selectedCardMachineId}
                canEdit={hasPermission('cardmachine.update')}
                onRequestEdit={(cardMachine) => {
                    setOpenViewDialog(false);
                    setSelectedCardMachineId(null);
                    if (cardMachine?.ID) {
                        handleEditCardMachine(cardMachine);
                    }
                }}
            />
        </>
    );

    // Micro-label fixo do `tb-field` (DESIGN-SYSTEM.md §5.5/§9), igual a
    // `pages/services/List.js`.
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    // Shell mobile: lista do protótipo `maquininhas/lista.html` (mesmos dados, busca e ações).
    if (isMobileShell) {
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget={'Maquininhas'} openSnackbar={openSnackbar} message={snackbarMessage} setOpenSnackbar={setOpenSnackbar} severity={snackbarSeverity}>
                <MobileCrudList
                    items={filteredData}
                    getKey={(row) => row.ID}
                    getRow={(row) => ({
                        icon: CardIcon,
                        tone: 'green',
                        title: row.Name,
                        sub: `Débito ${taxText(row.TaxDebit)}% · PIX ${taxText(row.TaxPixQrCode)}%`,
                        end: <KitPill tone="success">{`até ${row.MaxAmountInstallment}x`}</KitPill>,
                    })}
                    loading={loading}
                    search={search}
                    onSearchChange={handleChangeSearch}
                    searchPlaceholder="Buscar maquininha…"
                    emptyIcon={CardIcon}
                    emptyTitle="Nenhuma maquininha encontrada"
                    emptyText="Ajuste a busca para ver outras maquininhas."
                    fabLabel={hasPermission('cardmachine.create') ? 'Nova' : undefined}
                    onNew={handleNewCardMachine}
                    rowTitle={(row) => row.Name}
                    rowActions={(row) => [
                        ...(hasPermission('cardmachine.view') ? [{ label: 'Visualizar', icon: EyeIcon, onClick: () => { setSelectedCardMachineId(row.ID); setOpenViewDialog(true); } }] : []),
                        ...(hasPermission('cardmachine.update') ? [{ label: 'Editar', icon: EditIcon, onClick: () => handleEditCardMachine(row) }] : []),
                        ...(hasPermission('cardmachine.delete') ? [{ label: 'Excluir', icon: TrashIcon, destructive: true, onClick: () => { setCardMachineToDelete(row.ID); setCardMachineNameToDelete(row.Name); setOpenDialogConfirmDelete(true); } }] : []),
                    ]}
                />
                {sharedDialogs}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Maquininhas'} openSnackbar={openSnackbar} message={snackbarMessage} setOpenSnackbar={setOpenSnackbar} severity={snackbarSeverity} >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ação (Nova maquininha). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <CreditCard size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Maquininhas
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Consulte as maquininhas de cartão cadastradas e suas taxas de operação.
                            </Typography>
                        </Box>
                    </Box>

                    {hasPermission('cardmachine.create') && (
                        <NewItemButton label="Nova maquininha" onClick={handleNewCardMachine} />
                    )}
                </Box>

                {/* report-toolbar — apenas o campo Buscar (sem outros filtros pedidos). */}
                <FilterToolbar
                    plain
                    primary={(
                        <Box sx={{ flex: '0 0 280px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome da maquininha…"
                                value={search}
                                onChange={(e) => handleChangeSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                        </Stack>
                    )}
                />

                {/* chips-row — filtro ativo removível + contagem. */}
                <FilterChips>
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={() => handleChangeSearch('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${filteredData.length} maquininha${filteredData.length === 1 ? '' : 's'} encontrada${filteredData.length === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={filteredData}
                    iconsActions={actions}
                    loading={loading}
                    rowPerPage={8}
                    rowsPerPageOptions={[5, 8, 15]}
                    itemLabel="maquininhas"
                    sortableFields={['Name']}
                    defaultSort={{ field: 'Name', direction: 'asc' }}
                />
            </Box>

            {sharedDialogs}
        </PageScaffold>
    );
}
